import React from "react";
import Marquee from "react-fast-marquee";
import { useReducedMotion } from "framer-motion";
import { Smartphone, MailCheck } from "lucide-react";
import { Reveal, ExternalLinkIcon, SectionLabel } from "./primitives";
import { businessDetails, marqueeItems } from "../data/content";
import { track } from "../lib/analytics";

const ext = { target: "_blank", rel: "noopener noreferrer" };

const cards = [
  {
    icon: Smartphone,
    title: "Customer App",
    desc: "Access the online services available to Freeman Jones IVA customers.",
    cta: "Open Customer App",
    url: businessDetails.customerAppUrl,
    event: "customer_app_clicked",
    testid: "panel-app",
  },
  {
    icon: MailCheck,
    title: "Digital Postbox",
    desc: "Access your Freeman Jones digital correspondence securely online.",
    cta: "Open Digital Postbox",
    url: businessDetails.digitalPostboxUrl,
    event: "digital_postbox_clicked",
    testid: "panel-postbox",
  },
];

export const CustomerActionPanel = () => (
  <section
    id="customer-services"
    aria-labelledby="customer-services-heading"
    className="relative z-10 -mt-2 bg-brand-bg pb-4"
  >
    <div className="mx-auto max-w-content px-4 lg:px-8">
      <Reveal className="rounded-[28px] border border-brand-border bg-white p-6 shadow-lg sm:p-8 lg:p-10">
        <div className="mb-7 max-w-2xl">
          <SectionLabel>Customer services</SectionLabel>
          <h2
            id="customer-services-heading"
            className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
          >
            Manage your Freeman Jones arrangement
          </h2>
          <p className="mt-3 text-lg text-brand-body">
            Use our secure online services to access your account or manage your Freeman
            Jones correspondence.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                className="group flex flex-col rounded-2xl border border-brand-border bg-brand-bg p-6 transition-transform duration-300 hover:-translate-y-1 sm:p-8"
                data-testid={`${c.testid}-card`}
              >
                <div className="flex items-center justify-between">
                  <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-teal/10 text-brand-teal">
                    <Icon className="h-7 w-7" aria-hidden="true" />
                  </span>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-teal">
                    Customer service
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-2xl font-bold text-brand-navy">
                  {c.title}
                </h3>
                <p className="mt-2 flex-1 text-base leading-relaxed text-brand-body">
                  {c.desc}
                </p>
                <a
                  {...ext}
                  href={c.url}
                  onClick={() => track(c.event, { location: "action-panel" })}
                  className="mt-6 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-brand-teal px-6 text-base font-semibold text-white transition-colors hover:bg-brand-tealHover"
                  data-testid={`${c.testid}-button`}
                  aria-label={`${c.cta} — opens external customer service in a new tab`}
                >
                  {c.cta} <ExternalLinkIcon />
                </a>
              </div>
            );
          })}
        </div>
      </Reveal>
    </div>
  </section>
);

export const EthosMarquee = () => {
  const reduce = useReducedMotion();
  return (
    <div className="border-y border-brand-border bg-brand-navy py-5" aria-hidden="true">
      <div className="fade-edges">
        <Marquee speed={reduce ? 0 : 26} gradient={false} autoFill pauseOnHover>
          {marqueeItems.map((item, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8">
              <span className="font-heading text-xl font-semibold tracking-tight text-white/90 lg:text-2xl">
                {item}
              </span>
              <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" />
            </span>
          ))}
        </Marquee>
      </div>
    </div>
  );
};
